import React from "react";
import {
  ChevronDown,
  ChevronUp,
  TerminalSquare,
  Eraser,
  Wifi,
  WifiOff,
  Keyboard,
  Download,
  Cpu,
} from "lucide-react";
import LiveTerminal from "./LiveTerminal";

function fmtMem(kb) {
  if (!kb || kb <= 0) return "";
  if (kb < 1024) return `${kb}KB`;
  return `${(kb / 1024).toFixed(1)}MB`;
}

export default function BottomTerminal({
  collapsed,
  onToggle,
  status,
  onClear,
  terminalRef,
  onKeyInput,
  themeId = "brand",
  onDownload,
  execStats,     // {exec_time_ms, peak_memory_kb, exit_code}
}) {
  const dotColor =
    status === "connected"
      ? "bg-emerald-400"
      : status === "error"
      ? "bg-rose-400"
      : "bg-ide-mute";

  const isInteractive = !!onKeyInput;
  const hasStats = execStats && (execStats.exec_time_ms != null);

  return (
    <div className="flex h-full flex-col bg-ide-panel2 border-t border-ide-border">
      <div className="flex items-center px-3 py-1.5 border-b border-ide-border text-[11px] uppercase tracking-wider text-ide-mute select-none">
        <button
          className="flex items-center gap-1.5 hover:text-ide-text transition-colors"
          onClick={onToggle}
          title={collapsed ? "Expand terminal (Ctrl+\u0060)" : "Collapse terminal (Ctrl+\u0060)"}
        >
          <TerminalSquare className="h-3.5 w-3.5" />
          terminal
        </button>
        <span
          className={
            "ml-2 inline-block h-1.5 w-1.5 rounded-full " + dotColor +
            (status === "connected" ? " animate-pulse-dot" : "")
          }
          title={
            status === "connected" ? "streaming" :
            status === "error" ? "error" : "idle"
          }
        />
        <span className="ml-2 normal-case font-mono text-[10px] text-ide-mute/70 hidden sm:inline">
          {status === "connected" ? (
            <span className="inline-flex items-center gap-1"><Wifi className="h-3 w-3" /> live</span>
          ) : status === "error" ? (
            <span className="inline-flex items-center gap-1"><WifiOff className="h-3 w-3" /> disconnected</span>
          ) : (
            <span>idle</span>
          )}
        </span>

        {/* Interactive mode badge */}
        {isInteractive && (
          <span className="ml-2 normal-case inline-flex items-center gap-1 text-[10px] font-mono text-cyan-400 border border-cyan-400/30 bg-cyan-400/10 px-1.5 py-0.5 rounded animate-pulse-dot">
            <Keyboard className="h-2.5 w-2.5" />
            input active
          </span>
        )}

        {/* Execution stats */}
        {hasStats && !isInteractive && (
          <span
            className="ml-2 normal-case inline-flex items-center gap-1 text-[10px] font-mono text-ide-mute hidden sm:inline-flex"
            data-testid="exec-stats"
            title={`exit ${execStats.exit_code ?? 0} · ${execStats.exec_time_ms}ms${execStats.peak_memory_kb ? ' · ' + fmtMem(execStats.peak_memory_kb) : ''}`}
          >
            <Cpu className="h-2.5 w-2.5" />
            <span>{execStats.exec_time_ms}ms</span>
            {execStats.peak_memory_kb > 0 && (
              <span>· {fmtMem(execStats.peak_memory_kb)}</span>
            )}
          </span>
        )}

        <div className="flex-1" />
        <button
          className="flex items-center gap-1.5 hover:text-ide-text transition-colors px-1.5 py-0.5 rounded hover:bg-ide-panel"
          onClick={onDownload}
          title="Download output as .txt"
          data-testid="download-output-btn"
        >
          <Download className="h-3.5 w-3.5" />
          <span className="normal-case hidden sm:inline">Save</span>
        </button>
        <button
          className="ml-1 flex items-center gap-1.5 hover:text-ide-text transition-colors px-1.5 py-0.5 rounded hover:bg-ide-panel"
          onClick={onClear}
          title="Clear terminal"
        >
          <Eraser className="h-3.5 w-3.5" />
          <span className="normal-case">Clear</span>
        </button>
        <button
          className="ml-1 flex items-center hover:text-ide-text transition-colors px-1.5 py-0.5 rounded hover:bg-ide-panel"
          onClick={onToggle}
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
      <div
        className={"flex-1 min-h-0" + (isInteractive ? " ring-1 ring-cyan-400/20 ring-inset" : "")}
        style={{ display: collapsed ? "none" : "block" }}
      >
        <LiveTerminal ref={terminalRef} onKeyInput={onKeyInput} themeId={themeId} />
      </div>
    </div>
  );
}
